// components/ReleaseVersionViewer.jsx
// Display and manage app version/release history
import React, { useState, useEffect } from "react";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { getAllVersions, deleteVersionEntry } from "../utils/releaseVersions";
import VersionForm from "./VersionForm";

function VersionCard({ version, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);

  const formatDate = (date) => {
    if (!date) return "";
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString();
  };

  const changeTypeColors = {
    bug: "#ff6b6b",
    enhancement: "#74c0fc",
    other: "#999",
  };

  return (
    <div
      style={{
        background: "rgba(255,255,255,0.05)",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 12,
        padding: 16,
        marginBottom: 12,
        cursor: "pointer",
        transition: "background 0.2s",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.08)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.05)")}
      onClick={() => setExpanded(!expanded)}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div
            style={{
              minWidth: 80,
              padding: "6px 12px",
              borderRadius: 8,
              background: "#ffe066",
              color: "#111",
              fontWeight: 900,
              fontSize: "0.9em",
              textAlign: "center",
            }}
          >
            {version.version}
          </div>
          <div>
            <div style={{ fontWeight: 700, color: "#fff", fontSize: "1.1em" }}>
              {version.id}
            </div>
            <div style={{ fontSize: "0.85em", color: "#aaa", marginTop: 4 }}>
              Released: {formatDate(version.releaseDate)}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {onEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(version);
              }}
              style={{
                padding: "6px 12px",
                borderRadius: 6,
                border: "1px solid rgba(255,255,255,0.2)",
                background: "rgba(71,227,115,0.2)",
                color: "#47e373",
                fontWeight: 700,
                cursor: "pointer",
                fontSize: "0.85em",
              }}
            >
              Edit
            </button>
          )}
          {onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(`Delete version ${version.version}?`)) {
                  onDelete(version.id);
                }
              }}
              style={{
                padding: "6px 12px",
                borderRadius: 6,
                border: "1px solid rgba(255,255,255,0.2)",
                background: "rgba(255,107,107,0.2)",
                color: "#ff6b6b",
                fontWeight: 700,
                cursor: "pointer",
                fontSize: "0.85em",
              }}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
          {version.changes && version.changes.length > 0 ? (
            <div>
              <div style={{ fontWeight: 700, marginBottom: 12, color: "#ccc" }}>Changes:</div>
              {version.changes.map((change, i) => (
                <div
                  key={i}
                  style={{
                    padding: 10,
                    marginBottom: 8,
                    background: "rgba(0,0,0,0.2)",
                    borderRadius: 6,
                    borderLeft: `3px solid ${changeTypeColors[change.type] || changeTypeColors.other}`,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span
                      style={{
                        padding: "2px 8px",
                        borderRadius: 4,
                        background: changeTypeColors[change.type] || changeTypeColors.other,
                        color: "#fff",
                        fontSize: "0.75em",
                        fontWeight: 700,
                      }}
                    >
                      {change.type?.toUpperCase() || "OTHER"}
                    </span>
                    {change.logEntryId && (
                      <span style={{ fontSize: "0.75em", color: "#aaa" }}>
                        (Linked: {change.logEntryId})
                      </span>
                    )}
                  </div>
                  <div style={{ color: "#ddd", whiteSpace: "pre-wrap" }}>
                    {change.description || "No description"}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: "#aaa", fontStyle: "italic" }}>No changes documented</div>
          )}

          {version.notes && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontWeight: 700, marginBottom: 8, color: "#ccc" }}>Notes:</div>
              <div style={{ color: "#ddd", whiteSpace: "pre-wrap" }}>{version.notes}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ReleaseVersionViewer({ isQwizard }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVersion, setEditingVersion] = useState(null);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!isQwizard) return;

    const q = query(collection(db, "releaseVersions"), orderBy("releaseDate", "desc"));
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const vers = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          vers.push({
            id: docSnap.id,
            ...data,
            releaseDate: data.releaseDate?.toDate?.() || new Date(data.releaseDate),
            createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt),
            updatedAt: data.updatedAt?.toDate?.() || new Date(data.updatedAt),
          });
        });
        setVersions(vers);
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching versions:", err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [isQwizard]);

  if (!isQwizard) {
    return null;
  }

  const handleEdit = (version) => {
    setEditingVersion(version);
    setShowForm(true);
  };

  const handleDelete = async (versionId) => {
    try {
      await deleteVersionEntry(versionId);
    } catch (err) {
      console.error("Error deleting version:", err);
      alert("Failed to delete version");
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingVersion(null);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingVersion(null);
  };

  if (loading) {
    return (
      <div style={{ padding: 20, textAlign: "center", color: "#fff" }}>Loading versions...</div>
    );
  }

  return (
    <>
      <div style={{ marginTop: 40, marginBottom: 20 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <h2 style={{ margin: 0, color: "#ffe066" }}>Version History & Release Notes</h2>
          <button
            onClick={() => {
              setEditingVersion(null);
              setShowForm(true);
            }}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "none",
              background: "#47e373",
              color: "#111",
              fontWeight: 900,
              cursor: "pointer",
            }}
          >
            + New Version
          </button>
        </div>

        {versions.length === 0 ? (
          <div style={{ color: "#aaa", fontStyle: "italic", padding: 20, textAlign: "center" }}>
            No versions recorded yet. Create your first version to get started!
          </div>
        ) : (
          <div>
            {versions.map((version) => (
              <VersionCard
                key={version.id}
                version={version}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      <VersionForm
        open={showForm}
        onClose={handleFormClose}
        existingVersion={editingVersion}
        onSuccess={handleFormSuccess}
      />
    </>
  );
}
